import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/apiClient";
import { MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("Lien invalide ou incomplet."); return; }
    if (pwd.length < 6) { setError("Le mot de passe doit contenir au moins 6 caractères."); return; }
    if (pwd !== confirm) { setError("Les deux mots de passe ne correspondent pas."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, nouveau: pwd });
      setDone(true);
      toast.success("Mot de passe mis à jour");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 flex-col justify-between bg-[#0A0A0A] p-12 lg:flex">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600"><MapPin className="h-6 w-6 text-white" /></div>
          <span className="font-heading text-3xl font-black tracking-tighter text-white">CVT</span>
        </div>
        <div>
          <h1 className="font-heading text-4xl font-black leading-tight text-white">Nouveau<br />mot de passe</h1>
          <p className="mt-4 max-w-md text-slate-400">Définissez un nouveau mot de passe sécurisé pour accéder à votre espace CVT.</p>
        </div>
        <div className="text-xs text-slate-600">© 2026 CVT · Outil professionnel</div>
      </div>

      <div className="flex w-full items-center justify-center bg-white px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Réinitialiser le mot de passe</h2>
          <p className="mt-1 text-sm text-slate-500">Choisissez votre nouveau mot de passe.</p>

          {done ? (
            <div data-testid="reset-success" className="mt-8 flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              <CheckCircle2 className="h-5 w-5" /> Mot de passe mis à jour. Redirection vers la connexion…
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Nouveau mot de passe</label>
                <input data-testid="reset-password" type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-slate-900" placeholder="••••••••" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Confirmer le mot de passe</label>
                <input data-testid="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-slate-900" placeholder="••••••••" />
              </div>
              {error && <div data-testid="reset-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
              <button data-testid="reset-submit" type="submit" disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#0A0A0A] py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Définir le mot de passe
              </button>
              <Link to="/login" className="block w-full text-center text-xs text-slate-500 hover:text-slate-900">← Retour à la connexion</Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
